/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { reduxForm, Field } from 'redux-form';
import { capitalize } from 'lodash';
import { compose } from 'redux';
import { LabeledTextField } from '@nextiva/next-ui';
import { Button } from '@nextiva/next-ui-components';
import { cancelRequest } from 'utils/redux-axios-helpers';
import {
  ContentContainer,
  ContentRow,
  AsyncView,
  DebouncedAsyncSelect,
} from 'components';
import { Page } from 'containers';
import { withValue } from 'utils/redux-form-helpers';
import { required, maxLength, validString } from 'utils/validators';
import { getMessageFromError } from 'utils/meta-selectors';
import WithTooltip from 'containers/withPermissions/WithTooltips';

import { useSnackbar } from 'hooks';
import { getRole, saveRole, updateRole, cleanRole } from '../duck/actions';
import {
  getPreparedCurrentRole,
  isCurrentRoleDefault,
} from '../duck/selectors';
import { prepareRoleForSaving } from '../duck/helpers';

import { getProducts } from './RoleProducts/duck/actions';
import styles from './ManageRole.scss';

const EnhancedLabeledTextField = withValue(LabeledTextField);
const EnhancedDebouncedAsyncSelect = withValue(DebouncedAsyncSelect);
const max128 = maxLength(128);
const paramsBuilder = (search) => ({
  page_size: 50,
  order_by_asc: true,
  sort_attribute: 'name',
  filter_by_name: search,
});

const optionsBuilder = ({ result }) => {
  const options = (result || []).map(({ name, id, description }) => ({
    label: name,
    value: id,
    description,
  }));

  return options;
};

const ManageRole = ({ handleSubmit, pristine, submitting, children }) => {
  const dispatch = useDispatch();
  const [actionType, setActionType] = useState(null);
  const [roleId, setRoleId] = useState(null);
  const [roleLoading, setRoleLoading] = useState(true);
  const loadError = useSelector((state) =>
    getMessageFromError(state.roles, 'Can not load selected role!'),
  );
  const initialValues = useSelector(getPreparedCurrentRole);
  const isDefaultRole = useSelector(isCurrentRoleDefault);
  const products = useSelector((state) => state.products);

  const setSnackbar = useSnackbar();
  useEffect(() => {
    const { hash } = window.location;
    const [, , actionTypeFromHash, paramRoleId] = hash.split('/');

    if (paramRoleId) setRoleId(paramRoleId.replace(/%20/g, ' '));

    if (!roleId && roleId !== 'roles') {
      dispatch(getRole(paramRoleId)).then(() => {
        setRoleLoading(false);
      });
      setActionType(actionTypeFromHash);
      setRoleId(roleId);
    }

    if (!products.list.length) {
      dispatch(getProducts(setSnackbar));
    }

    return () => {
      dispatch(cancelRequest());
      dispatch(cleanRole());
    };
  }, []);

  const save = (values) => {
    const model = prepareRoleForSaving(values, initialValues, actionType);

    if (actionType === 'edit') {
      return dispatch(updateRole(initialValues.role, model, setSnackbar));
    }

    return dispatch(saveRole(model, setSnackbar));
  };

  const submit = (values) => {
    save(values).then(() => {
      window.location.hash = '/advancedroles';
    });
  };

  const cancel = () => {
    window.location.hash = '/advancedroles';
  };

  return (
    <Page
      title={`${capitalize(actionType)} Role`}
      loading={roleLoading}
      error={loadError}
    >
      <ContentContainer
        component="form"
        className={styles.form}
        onSubmit={handleSubmit(submit)}
        data-testid="PlatformAdminUI-FieldsPermissions-ContentContainer"
      >
        <ContentRow>
          <div className={styles.inputsWrapper}>
            <div className={styles.roleName}>
              <WithTooltip showTootip={isDefaultRole} displayBlock>
                <Field
                  name="role.name"
                  validate={[required, max128, validString]}
                  component={EnhancedLabeledTextField}
                  label="Role Name"
                  labelAlign="out"
                  disabled={isDefaultRole}
                />
              </WithTooltip>
            </div>
            <div className={styles.baseRoles}>
              <Field
                baseUrl="/platform-admin/v1/roles/search"
                paramsBuilder={paramsBuilder}
                optionsBuilder={optionsBuilder}
                name="assigned_roles"
                component={EnhancedDebouncedAsyncSelect}
                label="Base Permissions on"
                labelAlign="out"
                multi
                closeOnSelect={false}
              />
            </div>
            <div className={styles.description}>
              <WithTooltip showTootip={isDefaultRole} displayBlock>
                <Field
                  name="role.description"
                  validate={[required, max128, validString]}
                  component={EnhancedLabeledTextField}
                  label="Description"
                  labelAlign="out"
                  disabled={isDefaultRole}
                />
              </WithTooltip>
            </div>
          </div>
        </ContentRow>
        <ContentRow className={styles.contentWrapper}>
          <AsyncView
            loading={products.loading}
            className={styles.contentWrapper}
          >
            {children}
          </AsyncView>
        </ContentRow>
        <ContentRow>
          <div className={styles.buttonsWrapper}>
            <Button
              type="button"
              color="secondary"
              variant="contained"
              onClick={cancel}
              data-testid="PlatformAdminUI-ManageRole-CancelButton"
            >
              Cancel
            </Button>
            <Button
              color="success"
              variant="contained"
              type="submit"
              className={styles.submitButton}
              disabled={pristine || submitting}
            >
              Save
            </Button>
          </div>
        </ContentRow>
      </ContentContainer>
    </Page>
  );
};

const enhanceWithReduxForm = reduxForm({
  form: 'role',
  keepDirtyOnReinitialize: false,
  enableReinitialize: true,
});

export default compose(enhanceWithReduxForm)(ManageRole);
